import React, { useState } from "react";
import { Copy, Download, ExternalLink, Link, Trash, PencilIcon } from "lucide-react";
import { File } from "@/types/breeze/File";
import { downloadFile } from "@/utils/fileUtils";
import { fileService } from "@/services/breeze/FileService";
import toast from "react-hot-toast";
import { Flex, IconButton, Button } from "@radix-ui/themes";

interface FileActionsProps {
  file: File;
  onDelete: () => void;
  onEdit?: () => void;
  minimal?: boolean;
  showDelete?: boolean;
}

export function FileActions({
  file,
  onDelete,
  onEdit,
  minimal = false,
  showDelete = true,
}: FileActionsProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const isImage = file.file_type.startsWith("image/");

  const handleCopyUrl = async () => {
    if (file.is_public && file.cdn_url) {
      // For public files, copy the CDN URL
      await navigator.clipboard.writeText(file.cdn_url);
      toast.success("Public link copied to clipboard! 🌐", {
        duration: 4000,
      });
      toast("✅ This link can be accessed by anyone without authentication", {
        duration: 6000,
        icon: "🔗",
      });
    } else {
      // For private files, copy the authenticated URL with warning
      const url = file.anythink_url;
      await navigator.clipboard.writeText(url);
      toast.success("URL copied to clipboard", {
        duration: 4000,
      });
      toast("⚠️ Note: This URL requires authentication to access", {
        duration: 6000,
      });
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadFile(file);
    } catch {
      toast.error("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewExternal = async () => {
    if (file.is_public && file.cdn_url) {
      window.open(file.cdn_url, "_blank");
      return;
    }
    try {
      const url = await fileService.getFileSignedUrl(file.id);
      window.open(url, "_blank", "noopener");
    } catch (error) {
      console.error("Failed to open file:", error);
      toast.error("Failed to open file in new tab");
    }
  };

  const buttonSize = minimal ? "1" : "2";
  const iconSize = minimal ? 16 : 18;

  // Determine the appropriate icon and title for the copy action
  const copyTitle = file.is_public ? "Copy public link" : "Copy URL";

  return (
    <Flex
      justify="between"
      align="center"
      style={{
        borderRadius: "var(--radius-2)",
        padding: "4px",
        width: "100%",
      }}
    >
      <IconButton
        variant="ghost"
        color="gray"
        size={buttonSize}
        onClick={(e) => {
          e.stopPropagation();
          handleCopyUrl();
        }}
        title={copyTitle}
        style={{
          cursor: "pointer",
          color: "var(--gray-11)",
        }}
      >
        {file.is_public ? <Link size={iconSize} /> : <Copy size={iconSize} />}
      </IconButton>

      <IconButton
        variant="ghost"
        color="gray"
        size={buttonSize}
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        disabled={isDownloading}
        title="Download"
        style={{
          cursor: "pointer",
          color: "var(--gray-11)",
          opacity: isDownloading ? 0.5 : 1,
        }}
      >
        <Download size={iconSize} />
      </IconButton>

      {isImage && (
        <IconButton
          variant="ghost"
          color="gray"
          size={buttonSize}
          onClick={(e) => {
            e.stopPropagation();
            handleViewExternal();
          }}
          title="View image"
          style={{
            cursor: "pointer",
            color: "var(--gray-11)",
          }}
        >
          <ExternalLink size={iconSize} />
        </IconButton>
      )}

      {onEdit && (
        <IconButton
          variant="ghost"
          color="gray"
          size={buttonSize}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Edit details"
          style={{ cursor: "pointer", color: "var(--gray-11)" }}
        >
          <PencilIcon size={iconSize} />
        </IconButton>
      )}

      {showDelete && (
        <IconButton
          variant="ghost"
          color="gray"
          size={buttonSize}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
          style={{
            cursor: "pointer",
            color: "var(--gray-11)",
          }}
        >
          <Trash size={iconSize} />
        </IconButton>
      )}
    </Flex>
  );
}

export function BulkFileActions({
  selectedFiles,
  onBulkDownload,
  onBulkDelete,
  onBulkReprobe,
}: {
  selectedFiles: File[];
  onBulkDownload: () => void;
  onBulkDelete: () => void;
  onBulkReprobe?: () => void;
}) {
  return (
    <Flex gap="2">
      <Button
        variant="outline"
        size="1"
        onClick={onBulkDownload}
        disabled={selectedFiles.length === 0}
      >
        Download {selectedFiles.length} selected
      </Button>

      {onBulkReprobe && (
        <Button
          variant="outline"
          size="1"
          onClick={onBulkReprobe}
          disabled={selectedFiles.length === 0}
        >
          Reprobe metadata
        </Button>
      )}

      <Button
        variant="solid"
        color="red"
        size="1"
        onClick={onBulkDelete}
        disabled={selectedFiles.length === 0}
      >
        Delete {selectedFiles.length} selected
      </Button>
    </Flex>
  );
}
